import moment from 'moment-timezone';
import { FALLBACK_TIME_ZONE } from './constants';

const TIME_PATTERN = /^(\d{2}):(\d{2})(?::(\d{2}))?$/;

export const isValidTimeZone = (name) => Boolean(name) && Boolean(moment.tz.zone(name));

export const resolveTimeZone = (name) => {
  if (isValidTimeZone(name)) return name;
  return FALLBACK_TIME_ZONE;
};

// "unitTime" values are UTC instants; parse them as UTC even when they carry no offset.
export const parseInstant = (value) => moment.utc(value).valueOf();

export const toDayKey = (ms, timeZone) => moment.tz(ms, timeZone).format('YYYY-MM-DD');

export const groupDayKeys = (timestamps, timeZone) => {
  const keys = new Set();
  timestamps.forEach((value) => {
    const ms = parseInstant(value);
    if (Number.isFinite(ms)) keys.add(toDayKey(ms, timeZone));
  });
  return [...keys].sort();
};

// Start and end of a calendar day in the unit zone; a DST day can be 23 or 25 hours long.
export const dayBounds = (dayKey, timeZone) => {
  const start = moment.tz(dayKey, 'YYYY-MM-DD', timeZone).startOf('day');
  const end = start.clone().endOf('day');
  return { startMs: start.valueOf(), endMs: end.valueOf() };
};

export const toUtcIso = (ms) => new Date(ms).toISOString();

export const formatClock = (ms, timeZone, withSeconds = false) => {
  let pattern = 'HH:mm';
  if (withSeconds) pattern = 'HH:mm:ss';
  return moment.tz(ms, timeZone).format(pattern);
};

export const formatDayLabel = (dayKey, locale) => {
  return moment.utc(dayKey, 'YYYY-MM-DD').locale(locale).format('ll');
};

export const formatDuration = (ms) => {
  const totalMinutes = Math.round(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes} min`;
  return `${hours} h ${minutes} min`;
};

export const formatClipLength = (ms) => {
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = String(totalSeconds % 60).padStart(2, '0');
  return `${minutes}:${seconds}`;
};

// Turns "HH:mm[:ss]" typed in the unit zone into an instant, on the day of `referenceMs`.
// On a repeated hour (DST fall back) it picks the occurrence closest to `referenceMs`.
export const clockToInstant = (text, referenceMs, timeZone) => {
  const match = TIME_PATTERN.exec(text || '');
  if (!match) return null;
  const [, hh, mm, ss = '00'] = match;
  const clock = `${hh}:${mm}:${ss}`;
  const dayKey = toDayKey(referenceMs, timeZone);
  const parsed = moment.tz(`${dayKey} ${clock}`, 'YYYY-MM-DD HH:mm:ss', timeZone);
  if (!parsed.isValid()) return null;

  const hour = 60 * 60 * 1000;
  const candidates = [parsed.valueOf() - hour, parsed.valueOf(), parsed.valueOf() + hour].filter(
    (ms) => formatClock(ms, timeZone, true) === clock
  );
  if (candidates.length === 0) return null;
  candidates.sort((a, b) => Math.abs(a - referenceMs) - Math.abs(b - referenceMs));
  return candidates[0];
};
